import { z } from 'zod';
import { emailSchema } from '../auth/email.js';
import { passwordSchema } from '../auth/password.js';
import type { Gender } from '../enums.js';

export const getAdminInstitutionByUserParamsSchema = z.object({
  user_id: z.uuid(),
});
export type GetAdminInstitutionByUserParamsPayload = z.infer<typeof getAdminInstitutionByUserParamsSchema>;

export const getAdminInstitutionParamsSchema = z.object({
  institution_id: z.uuid(),
});
export type GetAdminInstitutionParamsPayload = z.infer<typeof getAdminInstitutionParamsSchema>;

export const updateAdminInstitutionSchema = z.object({
  display_name: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  state: z.string().min(1).optional(),
  zip_code: z.string().min(1).optional(),
  country: z.string().min(1).optional(),
  company_registration: z.string().min(1).optional(),
});
export type UpdateAdminInstitutionPayload = z.infer<typeof updateAdminInstitutionSchema>;

export const offerInstitutionSeatSchema = z.object({
  email: emailSchema(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  phone: z.string().optional(),
  password: passwordSchema,
});
export type OfferInstitutionSeatPayload = z.infer<typeof offerInstitutionSeatSchema>;

export const addAdminInstitutionMemberSchema = offerInstitutionSeatSchema;
export type AddAdminInstitutionMemberPayload = z.infer<typeof addAdminInstitutionMemberSchema>;

export const removeInstitutionMemberParamsSchema = z.object({
  institution_id: z.uuid(),
  user_id: z.uuid(),
});
export type RemoveInstitutionMemberParamsPayload = z.infer<typeof removeInstitutionMemberParamsSchema>;

export const activateInstitutionTrialSchema = z.object({
  trial_end_at: z.iso.datetime(),
  max_seats: z.number().int().min(1).max(100).optional().nullable(),
});
export type ActivateInstitutionTrialPayload = z.infer<typeof activateInstitutionTrialSchema>;

export const updateInstitutionTrialSchema = z.object({
  trial_end_at: z.iso.datetime().optional(),
  max_seats: z.number().int().min(1).max(100).optional().nullable(),
});
export type UpdateInstitutionTrialPayload = z.infer<typeof updateInstitutionTrialSchema>;

export const listAdminInstitutionsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  status: z.enum(['none', 'trialing', 'active', 'paused', 'past_due', 'canceled', 'incomplete']).optional(),
});
export type ListAdminInstitutionsPayload = z.infer<typeof listAdminInstitutionsSchema>;

export interface AdminInstitutionMember {
  id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  gender: Gender | null;
  is_complimentary: boolean;
  created_at: string;
}

export interface AdminInstitutionDetail {
  id: string;
  owner_id: string;
  owner_email: string;
  display_name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  company_registration: string;
  subscription_status: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  trial_start_at: string | null;
  trial_end_at: string | null;
  trial_max_seats: number | null;
  has_payment_method: boolean;
  seat_count: number;
  complimentary_seat_count: number;
  billable_seat_count: number;
  members: AdminInstitutionMember[];
  created_at: string;
  updated_at: string;
}

export interface GetAdminInstitutionResponse {
  institution: AdminInstitutionDetail;
}

export interface OfferInstitutionSeatResponse {
  member: AdminInstitutionMember;
  message: string;
}

export interface AdminInstitutionTrialResponse {
  institution: AdminInstitutionDetail;
  message: string;
}

export interface AdminInstitutionListItem {
  id: string;
  display_name: string;
  owner_id: string;
  owner_email: string;
  city: string;
  subscription_status: string | null;
  seat_count: number;
  billable_seat_count: number;
  complimentary_seat_count: number;
  trial_end_at: string | null;
  trial_max_seats: number | null;
  created_at: string;
}

export interface ListAdminInstitutionsResponse {
  institutions: AdminInstitutionListItem[];
  total: number;
  page: number;
  limit: number;
}
